// Where the editor's maps live, behind one port. In production: this browser's localStorage (localMaps.ts)
// plus the built-in maps as read-only templates. Under the dev server: the repo's maps/ through its dev-only
// endpoints (devStore.ts), which can also switch the map baked into the build.
import type { MapData, MapIssue } from '../../core/map/schema.js';
import { localMaps, checkMapName, type LocalMaps } from '../localMaps.js';
import { BUILTIN_MAPS, builtinMap } from '../playableMaps.js';
import { t } from '../i18n/index.js';

export type ApiResult<T> = { ok: true; value: T } | { ok: false; issues: MapIssue[] };

export type MapStore = {
  kind: 'dev' | 'local';
  /** How a saved map is shown to the player: a repo path or a name in quotes. */
  label(name: string): string;
  /** null when `name` can name a saved map, else why not. */
  checkName(name: string): MapIssue | null;
  list(): Promise<ApiResult<{ maps: string[]; active?: string }>>;
  load(name: string): Promise<ApiResult<MapData>>;
  save(name: string, map: MapData): Promise<ApiResult<unknown>>;
  /** Read-only maps to start from («Сохранить как…» keeps a copy); empty under the dev server. */
  templates: string[];
  loadTemplate(name: string): ApiResult<MapData>;
  rename?(from: string, to: string): Promise<ApiResult<unknown>>;
  remove?(name: string): Promise<ApiResult<unknown>>;
  /** Dev only: bake this map into the next build. */
  makeActive?(name: string): Promise<ApiResult<{ active: string; changed: boolean }>>;
};

/* ---- this browser ---- */
export function localStore(maps: LocalMaps = localMaps()): MapStore {
  const async = <T>(r: ApiResult<T>) => Promise.resolve(r);
  return {
    kind: 'local',
    label: name => t('editor.quoted', { name }),
    checkName: checkMapName,
    list: () => async({ ok: true, value: { maps: maps.list().map(e => e.name) } }),
    load: name => async(maps.load(name)),
    save: (name, map) => async(maps.save(name, map)),
    templates: Object.keys(BUILTIN_MAPS),
    loadTemplate: builtinMap,
    rename: (from, to) => async(maps.rename(from, to)),
    remove: name => async(maps.remove(name)),
  };
}

declare const __DEV_MAP_API__: boolean | undefined;  // scripts/dev.ts's build defines it true; absent in production and under bun test

/** Under the dev server, the repo's maps/ (when its endpoints answer); everywhere else, this browser. The
    dev adapter is compiled only into the dev build, so production carries no client for the dev map api. */
export async function chooseMapStore(): Promise<MapStore> {
  if (typeof __DEV_MAP_API__ === 'boolean' && __DEV_MAP_API__) {
    const { devStore, devApiAnswers } = await import('./devStore.js');
    if (await devApiAnswers()) return devStore();
  }
  return localStore();
}
